import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

export default function Blog() {
    const [params, setParams] = useSearchParams();
    const { category: routeCategory } = useParams();
    const [posts, setPosts] = useState([]);
    const [cats, setCats] = useState([]);
    const cat = routeCategory || params.get("category") || "";

    useEffect(() => {
        const opts = { limit: 24 };
        if (cat) opts.category = cat;
        api.blog(opts).then((d) => setPosts(d.items || []));
        api.blogCategories().then((d) => setCats(d.categories || []));
    }, [cat]);

    const featured = posts[0];
    const rest = posts.slice(1);

    return (
        <>
            <SEO title="Studio Journal" description="Editorial from Liquor Studio in Clinton, MO — guides, local Missouri stories, recipes, and pairings." path="/blog" />

            <section style={{ paddingTop: 140, paddingBottom: 48, borderBottom: "1px solid var(--ls-border)" }}>
                <div className="ls-container">
                    <span className="eyebrow">Studio Journal</span>
                    <h1 className="font-display mt-4 text-white" style={{ fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 500, lineHeight: 1.02, letterSpacing: "-0.02em" }}>
                        Read from <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>the studio</em>
                    </h1>
                </div>
            </section>

            <section className="py-12">
                <div className="ls-container">
                    {cats.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-12" data-testid="blog-categories">
                            <button onClick={() => { setParams({}); }} className="btn-ghost" style={{ padding: "6px 14px", fontSize: 12, color: !cat ? "var(--ls-gold)" : "white", borderColor: !cat ? "var(--ls-gold)" : "var(--ls-border-strong)" }} data-testid="blog-cat-all">
                                <Link to="/blog" style={{ color: "inherit", textDecoration: "none" }}>All</Link>
                            </button>
                            {cats.map(c => (
                                <Link key={c.category} to={`/blog/category/${c.category}`} className="btn-ghost" style={{ padding: "6px 14px", fontSize: 12, color: cat === c.category ? "var(--ls-gold)" : "white", borderColor: cat === c.category ? "var(--ls-gold)" : "var(--ls-border-strong)", textDecoration: "none" }} data-testid={`blog-cat-${c.category.replace(/\s+/g,'-').toLowerCase()}`}>
                                    {c.category} ({c.count})
                                </Link>
                            ))}
                        </div>
                    )}

                    {featured && (
                        <Link to={`/blog/${featured.slug}`} className="block mb-16" data-testid="blog-featured">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center group">
                                <div style={{ aspectRatio: "4/3", overflow: "hidden", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}>
                                    <img src={featured.cover_image_url} alt={featured.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s" }} className="group-hover:scale-105" />
                                </div>
                                <div>
                                    <span className="pill">{featured.category}</span>
                                    <h2 className="font-display text-white mt-5" style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                                        {featured.title}
                                    </h2>
                                    <p className="mt-4" style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.6 }}>{featured.excerpt}</p>
                                    <div className="mt-5 flex items-center gap-3 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                        <Clock size={13} /> {featured.reading_minutes} min read · {new Date(featured.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                    </div>
                                    <span className="inline-flex items-center gap-2 mt-6 text-sm font-medium" style={{ color: "var(--ls-gold)" }}>
                                        Read article <ArrowRight size={14} />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
                        {rest.map(p => (
                            <Link key={p.slug} to={`/blog/${p.slug}`} className="cat-card block" data-testid={`blog-card-${p.slug}`}>
                                <div style={{ aspectRatio: "16/10", overflow: "hidden" }}>
                                    <img src={p.cover_image_url} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                </div>
                                <div className="p-6">
                                    <span className="pill" style={{ fontSize: 9, padding: "3px 8px" }}>{p.category}</span>
                                    <h3 className="font-display text-white mt-3" style={{ fontSize: 21, fontWeight: 500, lineHeight: 1.25 }}>{p.title}</h3>
                                    <p className="mt-3 text-sm" style={{ color: "var(--ls-text-secondary)", lineHeight: 1.5 }}>{p.excerpt}</p>
                                    <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                        <Clock size={12} /> {p.reading_minutes} min · {new Date(p.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
